/**
 * Sample-pack sync + Library filter helpers shared by Library / Teach / Today.
 */

import { createEntity, updateEntity } from '../data/entities';
import { SAMPLE_PACK_REVISION, SAMPLES } from './sampleLessons';
import { lessonVisibleForPlan } from './planTypes';
import { studioAudioQueuePatch } from './stepNarration';
import {
  sequenceDurationMins,
  sequencePoseCount,
  normalizeSequence,
} from './sequenceModel';
import { getLessonSequence } from './lessonSequence';

export const AUDIENCES = [
  { id: 'all', label: 'All ages' },
  { id: 'kids', label: 'Kids' },
  { id: 'adults', label: 'Adults' },
];

export const SKILLS = [
  { id: 'all', label: 'Any level' },
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

export const STYLES = [
  { id: 'all', label: 'All styles' },
  { id: 'hatha', label: 'Hatha' },
  { id: 'vinyasa', label: 'Vinyasa' },
  { id: 'ashtanga', label: 'Ashtanga' },
  { id: 'iyengar', label: 'Iyengar' },
  { id: 'yin', label: 'Yin' },
  { id: 'restorative', label: 'Restorative' },
  { id: 'kundalini', label: 'Kundalini' },
  { id: 'playful', label: 'Playful' },
];

export const STYLE_OPTIONS = STYLES.filter((s) => s.id !== 'all');

export function normalizeStyle(id) {
  const key = String(id || '').trim().toLowerCase();
  return STYLE_OPTIONS.some((s) => s.id === key) ? key : '';
}

export function styleLabel(id) {
  const key = normalizeStyle(id);
  return STYLE_OPTIONS.find((s) => s.id === key)?.label || id || '—';
}

function samplePayload(sample, existing) {
  const sequence = normalizeSequence(sample.sequence, { style: sample.style });
  const base = {
    title: sample.title,
    description: sample.description,
    audience: sample.audience,
    skillLevel: sample.skillLevel,
    durationMins: sample.durationMins,
    style: sample.style,
    props: sample.props,
    cues: sample.cues,
    cuesTeacher: sample.cuesTeacher,
    sequence,
    karmaPack: !!sample.karmaPack,
    samplePackRevision: SAMPLE_PACK_REVISION,
  };
  const queue = studioAudioQueuePatch(sample.cues, existing?.studioAudio);
  return queue ? { ...base, ...queue } : base;
}

/**
 * Upsert the 17 sample lessons by exact title.
 * @returns {Promise<{ created: number, updated: number }>}
 */
export async function syncSamplePack(lessons, { force = false } = {}) {
  let created = 0;
  let updated = 0;
  const list = lessons || [];
  for (const sample of SAMPLES) {
    const existing = list.find((l) => l.title === sample.title);
    if (existing) {
      const atRevision = existing.samplePackRevision === SAMPLE_PACK_REVISION;
      if (!force && atRevision) {
        const queueOnly = studioAudioQueuePatch(sample.cues, existing.studioAudio);
        if (queueOnly) {
          await updateEntity('yogaLesson', {}, existing.id, queueOnly);
          updated += 1;
        }
        continue;
      }
      await updateEntity('yogaLesson', {}, existing.id, samplePayload(sample, existing));
      updated += 1;
    } else {
      const payload = samplePayload(sample, null);
      await createEntity('yogaLesson', {}, {
        ...payload,
        durationMins: Number(payload.durationMins) || 30,
      });
      created += 1;
    }
  }
  return { created, updated };
}

export function filterLessons(lessons, { planType, audience = 'all', skill = 'all', style = 'all' } = {}) {
  if (!lessons) return null;
  return lessons.filter((l) => {
    if (!lessonVisibleForPlan(l, planType)) return false;
    if (audience !== 'all' && l.audience !== audience) return false;
    if (skill !== 'all' && l.skillLevel !== skill) return false;
    if (style !== 'all' && normalizeStyle(l.style) !== style) return false;
    return true;
  });
}

export function lessonStats(lesson, planType) {
  const seq = getLessonSequence(lesson, planType);
  const poseCount = sequencePoseCount(seq);
  const fromSeq = sequenceDurationMins(seq);
  const durationMins = fromSeq || Number(lesson?.durationMins) || 0;
  return { poseCount, durationMins, sequence: seq };
}

/** Pick a recommended class for Today. */
export function recommendLesson(lessons, profile, planType) {
  const visible = filterLessons(lessons, { planType }) || [];
  if (!visible.length) return null;

  const level = profile?.practiceLevel || null;
  const preferred = Number(profile?.preferredMins) || 0;

  const scored = visible.map((l) => {
    const { durationMins } = lessonStats(l, planType);
    let score = 0;
    if (level && l.skillLevel === level) score += 40;
    if (preferred > 0) {
      const delta = Math.abs(durationMins - preferred);
      score += Math.max(0, 30 - delta);
    }
    if (l.karmaPack && planType === 'karma') score += 10;
    if (l.skillLevel === 'beginner' && (!level || level === 'beginner')) score += 5;
    if (l.style === 'hatha' && (!level || level === 'beginner')) score += 3;
    return { lesson: l, score, durationMins };
  });

  scored.sort((a, b) => b.score - a.score || a.durationMins - b.durationMins);
  return scored[0]?.lesson || visible[0];
}
